import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ArrowRightAltRounded } from '@mui/icons-material';
import { Box, CircularProgress } from '@mui/material';
import { styled } from '@mui/material';

import { INITIAL_STEP } from '~/components/organisms/stepper/constants';
import { Button } from '~/components/ui/button';
import { PREVIEW_BAR_VIEW_TYPE } from '~/constants/common';
import { PATH } from '~/constants/route-path';
import { useAppDispatch } from '~/hooks/useRedux';
import { qrCodeSlice } from '~/store/reducers/qr-code';
import { getQrCodeReducerData } from '~/store/reducers/qr-code/selectors';
import { getStepperData } from '~/store/reducers/stepepr/selectors';
import useBuilderStore from '~/store/useBuilderStore';
import useUrlValidationStore from '~/store/useUrlValidationStore';
import { COLORS } from '~/styles/colors';

type NavigationBottomProps = {
  handleNext?: () => void;
  handleBack?: () => void;
  isValid?: boolean;
  isNextProcessing: boolean;
  handleStep?: (value: number) => void;
};
export default function NavigationBottom({
  isNextProcessing,
  handleNext,
  handleBack,
  isValid = true,
  handleStep,
}: NavigationBottomProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { isMicrositeDataValid } = useSelector(getStepperData); //TODO check how it works
  const { data } = useSelector(getQrCodeReducerData);
  const { isPending: isUrlValidationPending } = useUrlValidationStore();
  const { activeStep, setActiveStep, setPreviewViewType } = useBuilderStore();

  const onHandleNext = () => {
    if (handleNext) {
      handleNext();
    } else if (handleStep) {
      handleStep(activeStep + 1);
    } else {
      setActiveStep(activeStep + 1);
    }
  };

  const onHandleBack = () => {
    if (handleBack) {
      handleBack();
    } else if (handleStep) {
      handleStep(activeStep - 1);
    } else {
      setActiveStep(activeStep - 1);
    }

    // TODO remove in future, data.type exists only for old QRs (website, vCard, social-media, images, app, links, pdf)
    if (activeStep === 1 && data.type && data.type !== 'DYNAMIC') {
      navigate(PATH.QR.CREATE);
      dispatch(qrCodeSlice.actions.resetState());
      if (setActiveStep) {
        setActiveStep(0);
      }
    }

    if (activeStep === 2) {
      setPreviewViewType(PREVIEW_BAR_VIEW_TYPE.PHONE);
    }
  };

  const getNextStepEndIcon = () => {
    if (isNextProcessing) {
      return <CircularProgress size={20} />;
    }

    if (isLastStep) {
      return undefined;
    }

    return <ArrowRightAltRounded />;
  };

  const isLastStep = activeStep === 2;
  const disableNext = !isMicrositeDataValid || !activeStep || !isValid || isNextProcessing || isUrlValidationPending;

  if (activeStep === INITIAL_STEP) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'sticky',
        bottom: 0,
        background: COLORS.white,
        padding: '16px 310px 16px 56px',
        display: 'flex',
        justifyContent: 'space-around',
        margin: '0 -10px 0 -20px',
        boxShadow: '0 -4px 20px 0px #0000001A',
        zIndex: 1000,
      }}
    >
      <StyledButton
        startIcon={<ArrowRightAltRounded sx={{ rotate: '180deg' }} />}
        type="outlined"
        onClick={onHandleBack}
      >
        {t('createQrStep1:btn1')}
      </StyledButton>
      <StyledButton
        type="outlined"
        disabled={disableNext}
        $isLastStep={isLastStep}
        $isNextButton
        endIcon={getNextStepEndIcon()}
        onClick={onHandleNext}
      >
        {isLastStep ? t('createQrStep1:btn3') : t('createQrStep1:btn2')}
      </StyledButton>
    </Box>
  );
}

const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== '$isLastStep' && prop !== '$isNextButton',
})<{ $isLastStep?: boolean; $isNextButton?: boolean }>(({ $isLastStep, $isNextButton }) => {
  let backgroundColor: string | undefined;
  let borderColor: string = COLORS.textMain;
  let color: string = COLORS.textMain;

  if ($isNextButton) {
    backgroundColor = COLORS.primary;
    borderColor = COLORS.primary;
    color = COLORS.white;
  }

  if ($isLastStep) {
    backgroundColor = COLORS.greenMain;
    borderColor = COLORS.greenMain;
    color = COLORS.white;
  }

  return {
    padding: '8px 28px !important',
    width: 'fit-content',
    borderColor: `${borderColor} !important`,
    color: `${color} !important`,
    backgroundColor: backgroundColor ? `${backgroundColor} !important` : undefined,
    '&:disabled': {
      borderColor: `${COLORS.grey400} !important`,
      color: `${COLORS.grey400} !important`,
      backgroundColor: `${COLORS.grey200} !important`,
    },
  };
});
